import React, {useState} from 'react';
import PropTypes from 'prop-types';
import infoCircle from '@adsk/bim360-matrix-react-assets/info-circle.svg';
import SimpleModal from '@adsk/bim360-matrix-react-simple-modal';
import Button from '@adsk/bim360-matrix-react-button';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import TextInput from '@adsk/bim360-matrix-react-text-input';
import Field from '@adsk/bim360-matrix-react-field';
import {NotificationTypes, NotificationLevels} from '@adsk/bim360-matrix-react-notification-manager';
import i18n from '../../utils/i18n';
import {createNewBcfFileAction, showNotificationAction} from '../../app/actions';
import {connect} from '../../app/connect';
import {BULK, SINGLE} from '../../utils/analytics-helpers';
import {ISSUE_MORE_THAN} from '../../constants/bcf-report';
import {getCurrentDocument} from '../../app/selectors';
import {getFileName} from '../../utils/bcf-helpers';

const Footer = ({
  creating,
  setCreating,
  fileName,
  createNewBcfFile,
  showNotification,
  closeModal,
  issuePreviewPage
}) => (
  <div>
    <Button styleType={Button.StyleType.LINK_SECONDARY} onClick={() => closeModal()}>
      {i18n.t('navigation.cancel')}
    </Button>
    <Button
      styleType={Button.StyleType.PRIMARY}
      disabled={creating || !fileName}
      onClick={() => {
        setCreating(true);
        createNewBcfFile(fileName, issuePreviewPage ? SINGLE : BULK)
          .then(() => {
            setCreating(false);
            closeModal();
            showNotification({
              type: NotificationTypes.TOAST,
              level: NotificationLevels.SUCCESS,
              message: i18n.t('exportBCF.successMessage', {fileName})
            });
          })
          .catch(err => {
            setCreating(false);
            closeModal();
            const supportedNumberOfIssues = err?.response?.data?.details.split(' ')?.[3];
            showNotification({
              type: NotificationTypes.TOAST,
              level: NotificationLevels.ERROR,
              message:
                err?.response?.data?.details === ISSUE_MORE_THAN.concat(supportedNumberOfIssues)
                  ? i18n.t('exportBCF.errorIssueLimitExceed', {supportedNumberOfIssues})
                  : i18n.t('exportBCF.errorMessage')
            });
          });
      }}
    >
      {creating ? i18n.t('new.creating') : i18n.t('export.exportAsBCF')}
    </Button>
  </div>
);

const BcfModal = ({
  closeModal,
  createNewBcfFileAction: createNewBcfFile,
  showNotificationAction: showNotification,
  issue,
  showIssueNameInFileName = true,
  container,
  currentDocument
}) => {
  const [fileName, setFileName] = useState(getFileName(issue, showIssueNameInFileName, currentDocument));
  const [creating, setCreating] = useState(false);

  return (
    <SimpleModal
      container={container}
      title={i18n.t('export.exportAsBCF')}
      onExited={() => closeModal()}
      footer={<Footer
        creating={creating}
        setCreating={setCreating}
        fileName={fileName.trim()}
        createNewBcfFile={createNewBcfFile}
        showNotification={showNotification}
        closeModal={closeModal}
        issuePreviewPage={showIssueNameInFileName}
      />}
    >
      <Field className="ExportModal__title-field" label={i18n.t('exportBCF.fileName')}>
        <TextInput value={fileName} onChange={value => setFileName(value)} />
      </Field>
      <div className="ExportModal__information">
        <SvgIcon>{infoCircle}</SvgIcon>
        <span>{i18n.t('exportBCF.modalInformation')}</span>
      </div>
    </SimpleModal>
  );
};

BcfModal.displayName = 'BcfModal';
Footer.displayName = 'Footer';

BcfModal.propTypes = {
  showIssueNameInFileName: PropTypes.bool,
  closeModal: PropTypes.func.isRequired,
  createNewBcfFileAction: PropTypes.func.isRequired,
  showNotificationAction: PropTypes.func.isRequired,
  issue: PropTypes.string,
  container: PropTypes.object,
  currentDocument: PropTypes.object
};

Footer.propTypes = {
  fileName: PropTypes.string,
  closeModal: PropTypes.func.isRequired,
  createNewBcfFile: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
  creating: PropTypes.bool,
  setCreating: PropTypes.func,
  issuePreviewPage: PropTypes.bool
};

const mapStateToProps = state => ({
  currentDocument: getCurrentDocument(state)
});

const mapDispatchToProps = {
  createNewBcfFileAction,
  showNotificationAction
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(BcfModal);
